import React, { useEffect, useState } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";

import { TrashIconPlus, WarnRedIcon } from "@components/Icons";
import Button from "@components/Button";
import Modal from "@components/Modal";
import useDialogAlert from "@hooks/useDialogAlert";

import { TypographyTitle, TypographyCaption } from "../typography";

interface Props {
  children?: React.ReactNode;
  scheduleID: string;
  isDisabled?: boolean;
  handleCancelScheduling: () => Promise<void>;
  hiddenIcon?: boolean;
  openExternal?: boolean;
  onClose?: () => void;
}

const ModalCancelScheduling: React.FC<Props> = ({
  children,
  openExternal,
  onClose,
  ...props
}) => {
  const { scheduleID, isDisabled, hiddenIcon, handleCancelScheduling } = props;
  const { snackbar } = useDialogAlert();

  const [load, setLoad] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (openExternal !== undefined) {
      setModalOpen(openExternal);
    }
  }, [openExternal]);

  const handleLocalClose = () => {
    setModalOpen(false);
    if (onClose) onClose();
  };

  const onClickConfirm = async () => {
    try {
      setLoad(true);
      await handleCancelScheduling();
      handleLocalClose();
    } catch (error) {
      console.error("Erro ao cancelar agendamento:", error);
      snackbar({
        message: "Erro ao cancelar agendamento. Tente novamente.",
        variant: "error",
      });
    } finally {
      setLoad(false);
    }
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleLocalClose}
      hiddenButton={!!openExternal}
      ModalButton={
        !openExternal ? (
          <Tooltip
            title={
              <TypographyCaption sx={{ fontSize: 10 }}>
                {isDisabled
                  ? "Você não pode cancelar um agendamento que possua alguma ordem de venda faturada"
                  : "Cancelar Agendamento"}
              </TypographyCaption>
            }
          >
            <span>
              <IconButton
                disabled={isDisabled}
                onClick={() => setModalOpen(true)}
                sx={{
                  opacity: isDisabled ? "0.4" : "1",
                  p: { xs: "4px", md: "8px" },
                  gap: 1,
                  borderRadius: "0",
                  "&:hover": { backgroundColor: "transparent" },
                }}
              >
                {children}
                {!hiddenIcon && (
                  <TrashIconPlus
                    color="primary"
                    sx={{
                      width: { xs: "2rem", md: "19px", lg: "22px" },
                      height: { xs: "2rem", md: "19px", lg: "22px" },
                    }}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : undefined
      }
    >
      {() => (
        <Box
          className={"Box-Cancel-Scheduling"}
          sx={{
            py: { xs: 4, md: 1 },
            px: { xs: 2, md: 4 },
            width: { xs: "280px", md: "420px" },
            textAlign: "center",
          }}
        >
          <Grid container direction="column" alignItems="center" gap={3}>
            <Grid item xs={12}>
              <WarnRedIcon style={{ width: 80, height: 80 }} />
            </Grid>
            <Grid item xs={12}>
              <TypographyTitle>
                Tem certeza que deseja cancelar o agendamento{" "}
                <b>
                  <u style={{ textDecorationColor: "red" }}>{scheduleID}</u>
                </b>
                ?
              </TypographyTitle>
            </Grid>

            <Grid item xs={12}>
              <TypographyCaption>
                Ao cancelar, as ordens de venda não estarão mais agendadas e
                retornarão à lista de "Não agendados".
              </TypographyCaption>
            </Grid>

            <Grid container xs={12} justifyContent="center" spacing={2}>
              <Grid item>
                <Button
                  disabled={load}
                  variant="outlined"
                  onClick={handleLocalClose}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button
                  loading={load}
                  variant="contained"
                  onClick={onClickConfirm}
                >
                  Confirmar
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}
    </Modal>
  );
};

export default ModalCancelScheduling;
